import React, { useCallback, useEffect, useMemo, useState } from "react";
import ObjectTransformChrome from "../../../../components/objectTransform/ObjectTransformChrome";
import { useObjectTransformInteraction } from "../../../../components/objectTransform/useObjectTransformInteraction";
import "./ToolPrepSubjectBoundingBox.css";

function roundFrame(frame) {
  if (!frame) return null;
  const x = Math.round(Number(frame.x ?? frame.left) || 0);
  const y = Math.round(Number(frame.y ?? frame.top) || 0);
  const width = Math.round(Number(frame.width) || 0);
  const height = Math.round(Number(frame.height) || 0);
  if (!(width > 0) || !(height > 0)) return null;
  return {
    x,
    y,
    width,
    height,
    left: x,
    top: y,
    rotation: Number(frame.rotation) || 0,
  };
}

/**
 * Tool-prep subject BB — Editor-like select / hover / deselect + transform.
 * - Click empty stage → hide BB
 * - Hover subject when deselected → dashed outline
 * - Click subject → select again (full handles)
 */
const ToolPrepSubjectBoundingBox = ({
  enabled = false,
  frame = null,
  crop = null,
  cropExtent = null,
  originalImageSize = null,
  onChange = null,
  onCroppingChange = null,
  interactive = false,
  stageSize = null,
  minSize = 10,
  rotation: rotationOverride = null,
  flipX = 1,
  flipY = 1,
  showRotate = true,
}) => {
  const canInteract = Boolean(interactive && typeof onChange === "function");
  const [selected, setSelected] = useState(true);
  const [hovered, setHovered] = useState(false);

  const stableFrame = useMemo(() => {
    const base = roundFrame(frame);
    if (!base) return null;
    if (rotationOverride != null) {
      return { ...base, rotation: Number(rotationOverride) || 0 };
    }
    return base;
  }, [
    frame?.left,
    frame?.top,
    frame?.x,
    frame?.y,
    frame?.width,
    frame?.height,
    frame?.rotation,
    rotationOverride,
  ]);

  // New interactive session → start selected (Editor opens with object selected).
  useEffect(() => {
    if (enabled && canInteract) {
      setSelected(true);
      setHovered(false);
    }
  }, [enabled, canInteract]);

  const handleChange = useCallback(
    (partial) => {
      if (typeof onChange !== "function") return;
      onChange({
        x: partial.x,
        y: partial.y,
        left: partial.x,
        top: partial.y,
        width: partial.width,
        height: partial.height,
        rotation:
          partial.rotation != null
            ? partial.rotation
            : stableFrame?.rotation || 0,
        crop: partial.crop,
      });
    },
    [onChange, stableFrame?.rotation]
  );

  const transformEnabled = canInteract && selected && Boolean(stableFrame);

  const {
    isDragging,
    isResizing,
    isRotating,
    isCropping,
    alignGuides,
    startMove,
    startResize,
    startRotate,
  } = useObjectTransformInteraction({
    enabled: transformEnabled,
    frame: stableFrame,
    crop,
    cropExtent,
    originalImageSize,
    stageSize,
    onChange: handleChange,
    minSize,
  });

  useEffect(() => {
    if (typeof onCroppingChange === "function") {
      onCroppingChange(Boolean(isCropping));
    }
  }, [isCropping, onCroppingChange]);

  const deselect = useCallback(() => {
    setSelected(false);
    setHovered(false);
  }, []);

  const selectSubject = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(true);
    setHovered(false);
  }, []);

  if (!enabled || !stableFrame) return null;

  const frameStyle = {
    left: `${stableFrame.x}px`,
    top: `${stableFrame.y}px`,
    width: `${stableFrame.width}px`,
    height: `${stableFrame.height}px`,
    transform: `rotate(${stableFrame.rotation}deg) scale(${flipX}, ${flipY})`,
    transformOrigin: "center center",
  };

  const showGuides =
    isDragging &&
    ((alignGuides?.vertical?.length || 0) > 0 ||
      (alignGuides?.horizontal?.length || 0) > 0);

  return (
    <>
      {canInteract ? (
        <div
          className="tool-prep-stage__deselect-hit"
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            deselect();
          }}
          aria-hidden="true"
        />
      ) : null}

      {showGuides ? (
        <div className="tool-prep-align-guides" aria-hidden="true">
          {(alignGuides.vertical || []).map((x) => (
            <div
              key={`v-${x}`}
              className="tool-prep-align-guides__v"
              style={{ left: `${x}px` }}
            />
          ))}
          {(alignGuides.horizontal || []).map((y) => (
            <div
              key={`h-${y}`}
              className="tool-prep-align-guides__h"
              style={{ top: `${y}px` }}
            />
          ))}
        </div>
      ) : null}

      {canInteract && !selected ? (
        <div
          className="tool-prep-subject-hit"
          style={frameStyle}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onMouseDown={selectSubject}
        />
      ) : null}

      {selected ? (
        <ObjectTransformChrome
          visible
          mode="selected"
          width={stableFrame.width}
          height={stableFrame.height}
          left={stableFrame.x}
          top={stableFrame.y}
          rotation={stableFrame.rotation}
          flipX={flipX}
          flipY={flipY}
          showRotate={showRotate}
          isDragging={isDragging}
          isResizing={isResizing}
          isRotating={isRotating}
          onBoxMouseDown={canInteract ? startMove : undefined}
          onResizeMouseDown={canInteract ? startResize : undefined}
          onRotateMouseDown={
            canInteract && showRotate ? startRotate : undefined
          }
        />
      ) : null}

      {!selected && hovered ? (
        <ObjectTransformChrome
          visible
          mode="hover"
          width={stableFrame.width}
          height={stableFrame.height}
          left={stableFrame.x}
          top={stableFrame.y}
          rotation={stableFrame.rotation}
          flipX={flipX}
          flipY={flipY}
        />
      ) : null}
    </>
  );
};

export default ToolPrepSubjectBoundingBox;
